/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, MenuItem, TextField, withStyles } from '@material-ui/core';
import { Button, Dialog, DialogActions, Typography } from '@mui/material';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { setPageTitle, showToast } from 'modules/layout/layout.actions';
import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import FullscreenSpinner from 'modules/common/fullscreenSpinner.component';
import AdvancedSearch from 'modules/common/advancedSearch.component';
import Spinner from 'modules/common/spinner.component';

import RecurringPayablesGrid from './recurringPayablesGrid.component';

import { getAllRecurringPayableSummaries, postRecurringPayable } from './store/recurringPayables.action';

import { getAuthorizationSummaries } from 'modules/authorizations/store/actions/authorizations.actions';
import { getVendors } from 'modules/authorizations/store/actions/dataManagement.actions';
import { GET_AUTHORIZATIONS_FAILURE } from 'modules/authorizations/store/types/authorizationTypes';
import { GET_VENDORS_FAILURE } from 'modules/authorizations/store/types/dataManagementTypes';

import { EXPENSES_PAYEE_TYPE, RECURRING_PAYABLE_STATUS, ZERO_GUID } from 'modules/enumerations/enumerationIds';
import { getAllEnumerations } from 'modules/enumerations/store/enumerationActions';
import { Permissions } from 'modules/layout/permissions';
import { hasPermission } from 'modules/layout/permissionService';

const styles = theme => ({
  headerContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-start',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1)
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    minWidth: 100
  }
});

const buildRecurringPayableCreateRequest = ({ clientId, payToVendor, vendorId, authorizationId }) => {
  const payeeTypeId = payToVendor ? EXPENSES_PAYEE_TYPE.VENDOR : EXPENSES_PAYEE_TYPE.TRANSFEREE;

  return {
    recurringPayableInformation: {
      recurringPayableId: 0,
      payeeId: null,
      recurringPayableStatusId: RECURRING_PAYABLE_STATUS.DRAFT,
      startDate: null,
      endDate: null,
      frequencyId: ZERO_GUID,
      numberOfPayments: null,
      cancelRecurringDate: null,
      hasSubmitToReview: false,
      clientId,
      invoiceNumber: '',
      amount: null,
      expenseAnalystId: null,
      description: '',
      invoiceDate: null,
      receiptCurrencyId: ZERO_GUID,
      payInCurrencyId: ZERO_GUID,
      currencyExchangeRate: null,
      currencyExchangeRateDate: null,
      batchTypeId: ZERO_GUID,
      onHoldReason: '',
      denyReason: '',
      entryDate: null,
      voucherTypeId: ZERO_GUID,
      isRushPayment: false,
      dueDate: null,
      payrollDate: null,
      notPayableId: ZERO_GUID,
      isWireTransfer: false,
      nextRunDate: null,
      addedBy: 1, // TODO: Replace with actual internal user ID
      addedOn: new Date(),
      modifiedBy: 1, // TODO: Replace with actual internal user ID
      modifiedOn: new Date()
    },
    payee: {
      payeeId: null,
      payeeTypeId,
      vendorId: payToVendor ? Number(vendorId) : null,
      authorizationId: !payToVendor ? Number(authorizationId) : null,
      transfereePayeeId: null,
      vendorPayeeId: null
    },
    addressInformation: null,
    bankingInformation: null,
    recurringPayableLineItems: []
  };
};

const PayToChoiceOptions = {
  Vendor: 'Vendor',
  EE: 'EE'
};

const RecurringPayablesView = props => {
  const {
    classes,
    history,
    recurringPayables,
    vendors,
    authorizationSummaries,
    clients,
    enumerations,
    setPageTitle,
    showToast,
    getAllVoucherRecurringPayable,
    postVoucherRecurringPayable,
    getVendors,
    getAuthorizationSummaries,
    getAllEnumerations
  } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClientName, setSelectedClientName] = useState(null);
  const [payToChoice, setPayToChoice] = useState(PayToChoiceOptions.Vendor);
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [selectedVendorName, setSelectedVendorName] = useState(null);
  const [selectedAuthId, setSelectedAuthId] = useState(null);
  const [selectedAuthName, setSelectedAuthName] = useState(null);

  const allEnums = useMemo(() => enumerations?.allEnums ?? {}, [enumerations]);

  const normalizedClients = useMemo(() => {
    const arr = Array.isArray(clients) ? clients : Object.values(clients ?? {});
    return arr.map(c => ({
      clientId: c.clientRecordId ?? c.id ?? c.clientId,
      clientName: c.shortName ?? c.legalName ?? c.clientName ?? c.description ?? ''
    }));
  }, [clients]);

  const clientAdvInfo = useMemo(
    () => ({
      searchingColumns: [
        { name: 'clientId', title: 'Client Id' },
        { name: 'clientName', title: 'Company Name' }
      ],
      searchingColumnExtensions: [
        { columnName: 'clientId', width: 150 },
        { columnName: 'clientName', width: 250 }
      ],
      searchingRows: normalizedClients,
      idKey: 'clientId',
      nameKey: 'clientName'
    }),
    [normalizedClients]
  );

  const provinces = useMemo(() => (Array.isArray(allEnums?.provinces) ? allEnums.provinces.filter(p => !!p?.isActive) : []), [allEnums]);
  const provinceTitleById = useMemo(() => {
    const map = {};
    for (const p of provinces) map[p.id] = p.title ?? '';
    return map;
  }, [provinces]);

  const normalizedVendors = useMemo(() => {
    const arr = Array.isArray(vendors) ? vendors : Object.values(vendors ?? {});
    return arr.map(v => {
      const stateId = v.stateId ?? v.provinceId ?? v.state ?? v.stateGuid ?? null;
      return { ...v, stateId, stateTitle: stateId ? provinceTitleById[stateId] ?? '' : '' };
    });
  }, [vendors, provinceTitleById]);

  const normalizedAuths = useMemo(() => {
    const full = a =>
      `${(a.transfereeFirstName ?? a.firstName ?? '').trim()} ${(a.transfereeLastName ?? a.lastName ?? '').trim()}`
        .trim()
        .replace(/\s+/g, ' ');
    return (authorizationSummaries ?? []).map(a => ({
      ...a,
      authorizationId: a.authorizationId ?? a.fileId ?? a.authId,
      transfereeName: a.transfereeName?.trim() ? a.transfereeName : full(a),
      clientName: a.clientName ?? ''
    }));
  }, [authorizationSummaries]);

  useEffect(() => {
    setPageTitle('Recurring Payables');
    (async () => {
      try {
        if (!enumerations?.allEnums) await getAllEnumerations();
        if (!vendors) {
          const { type } = await getVendors();
          if (type === GET_VENDORS_FAILURE)
            showToast('Failed to retrieve vendors, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        }
        if (!authorizationSummaries) {
          const { type } = await getAuthorizationSummaries();
          if (type === GET_AUTHORIZATIONS_FAILURE)
            showToast('Failed to retrieve authorizations, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        }
        if (!props.recurringPayables) {
          await getAllVoucherRecurringPayable();
        }
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const createRP = useCallback(async () => {
    setIsLoadingModal(true);

    const hasClient = !!selectedClientId;
    const hasPayee = payToChoice === 'Vendor' ? !!selectedVendorId : !!selectedAuthId;
    if (!hasClient || !hasPayee) {
      showToast('Please select a client and a valid payee (Vendor or Authorization).', { severity: 'error' });
      setIsLoadingModal(false);
      return;
    }

    const req = buildRecurringPayableCreateRequest({
      clientId: selectedClientId,
      payToVendor: payToChoice === 'Vendor',
      vendorId: selectedVendorId,
      authorizationId: selectedAuthId
    });

    const resp = await postVoucherRecurringPayable(req);
    const newId = resp?.response?.recurringPayableInformation?.recurringPayableId;
    if (!newId) {
      showToast('Failed to create recurring payable, please try again', { severity: 'error' });
      setIsLoadingModal(false);
      return;
    }

    setIsLoadingModal(false);
    history.push(`/recurringPayables/${newId}`);
  }, [selectedClientId, selectedVendorId, selectedAuthId, payToChoice, postVoucherRecurringPayable, showToast, history]);

  const canReadRecurringPayables = hasPermission(Permissions.Finance.RecurringPayables.Read);
  const canEditRecurringPayables = hasPermission(Permissions.Finance.RecurringPayables.Edit);

  if (!canReadRecurringPayables) {
    history.push('/');
    return null;
  }

  return (
    <>
      {isLoading ? (
        <FullscreenSpinner />
      ) : (
        <Box>
          <div className={classes.headerContainer}>
            <TextField
              select
              label="Clients"
              className={classes.textField}
              value={-1}
              onChange={e => {}}
              SelectProps={{ autoWidth: true }}
              variant="outlined"
            >
              <MenuItem value={-1}>
                <em>All Clients</em>
              </MenuItem>
              {normalizedClients
                .slice()
                .sort((a, b) => String(a.clientName).localeCompare(String(b.clientName)))
                .map(c => (
                  <MenuItem key={c.clientId} value={c.clientId}>
                    {c.clientName}
                  </MenuItem>
                ))}
            </TextField>
          </div>
          <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'flex-end', pr: '10px' }}>
            <Button
              variant="contained"
              color="primary"
              sx={{
                maxHeight: '40px',
                mr: '10px',
                mt: '8px',
                backgroundColor: '#5D9878',
                '&:hover': { backgroundColor: '#528569' }
              }}
              onClick={() => setShowCreate(true)}
              disabled={!canEditRecurringPayables}
            >
              Create Recurring Payable
            </Button>
          </Box>
          <div className="px-2">
            <RecurringPayablesGrid isLoading={isLoading} history={history} summaries={recurringPayables || []} allEnums={allEnums} />
          </div>
          <Dialog open={showCreate} fullWidth={false} maxWidth="lg">
            <Box sx={{ p: '25px' }}>
              <Typography variant="h5">New Recurring Payable</Typography>
            </Box>
            <Box sx={{ px: '25px', pt: 0 }}>
              <Typography variant="body2">Select from the base options below to get started.</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', p: '10px 20px' }}>
              {/* Client */}
              <Box sx={{ mr: '20px' }}>
                <AdvancedSearch
                  labelText="Client"
                  displayValueAccessorKey="clientName"
                  saveIdValueAccessorKey="clientId"
                  searchingCriteria={clientAdvInfo.searchingColumns}
                  searchingCriteriaExtensions={clientAdvInfo.searchingColumnExtensions}
                  searchingRows={normalizedClients}
                  id={selectedClientId}
                  value={selectedClientName}
                  setIdValue={(id, value) => {
                    setSelectedClientId(id);
                    setSelectedClientName(value);
                    setSelectedAuthId(null);
                    setSelectedAuthName(null);
                  }}
                  isReadOnly={false}
                />
                <Typography variant="caption" display="block" color="primary">
                  Required
                </Typography>
              </Box>
              <Box>
                <Button
                  color="secondary"
                  variant="contained"
                  sx={{
                    maxHeight: '40px',
                    ml: '12px',
                    mt: '8px',
                    backgroundColor: payToChoice === PayToChoiceOptions.Vendor ? '#36939b' : '#ffffff',
                    '&:hover': { backgroundColor: payToChoice === PayToChoiceOptions.Vendor ? '#15585e' : '#faf8f2' },
                    color: payToChoice === PayToChoiceOptions.Vendor ? '#ffffff' : '#080600'
                  }}
                  onClick={() => setPayToChoice(PayToChoiceOptions.Vendor)}
                >
                  Pay to Vendor
                </Button>
                <Button
                  color="secondary"
                  variant="contained"
                  sx={{
                    maxHeight: '40px',
                    ml: '12px',
                    mt: '8px',
                    backgroundColor: payToChoice === PayToChoiceOptions.EE ? '#36939b' : '#ffffff',
                    '&:hover': { backgroundColor: payToChoice === PayToChoiceOptions.EE ? '#15585e' : '#faf8f2' },
                    color: payToChoice === PayToChoiceOptions.EE ? '#ffffff' : '#080600'
                  }}
                  onClick={() => setPayToChoice(PayToChoiceOptions.EE)}
                >
                  Pay to EE
                </Button>
              </Box>
              {payToChoice === PayToChoiceOptions.Vendor ? (
                <Box sx={{ ml: '25px' }}>
                  <AdvancedSearch
                    labelText="Vendor"
                    displayValueAccessorKey="companyName"
                    saveIdValueAccessorKey="vendorId"
                    searchingCriteria={[
                      { name: 'vendorId', title: 'Vendor Id' },
                      { name: 'companyName', title: 'Company Name' },
                      { name: 'city', title: 'City' },
                      { name: 'stateTitle', title: 'State' },
                      { name: 'phone', title: 'Company Phone' },
                      { name: 'email', title: 'Company Email' }
                    ]}
                    searchingCriteriaExtensions={[
                      { columnName: 'vendorId', width: 150 },
                      { columnName: 'companyName', width: 250 },
                      { columnName: 'city', width: 175 },
                      { columnName: 'stateTitle', width: 140 },
                      { columnName: 'phone', width: 175 },
                      { columnName: 'email', width: 175 }
                    ]}
                    searchingRows={normalizedVendors}
                    id={selectedVendorId}
                    value={selectedVendorName}
                    setIdValue={(id, value) => {
                      setSelectedVendorId(id);
                      setSelectedVendorName(value);
                    }}
                    isReadOnly={false}
                  />
                  <Typography variant="caption" display="block" color="primary">
                    Required
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ ml: '25px' }}>
                  <AdvancedSearch
                    labelText="Authorization"
                    displayValueAccessorKey="transfereeName"
                    saveIdValueAccessorKey="authorizationId"
                    searchingCriteria={[
                      { name: 'authorizationId', title: 'Auth Id' },
                      { name: 'transfereeName', title: 'Full Name' },
                      { name: 'departure', title: 'Departure Location' },
                      { name: 'destination', title: 'Destination Location' },
                      { name: 'clientName', title: 'Client' }
                    ]}
                    searchingCriteriaExtensions={[
                      { columnName: 'authorizationId', width: 150 },
                      { columnName: 'transfereeName', width: 250 },
                      { columnName: 'departure', width: 175 },
                      { columnName: 'destination', width: 175 },
                      { columnName: 'clientName', width: 175 }
                    ]}
                    searchingRows={
                      selectedClientName ? normalizedAuths.filter(a => a.clientRecordId === selectedClientId) : normalizedAuths
                    }
                    id={selectedAuthId}
                    value={selectedAuthName}
                    setIdValue={(id, value) => {
                      setSelectedAuthId(id);
                      setSelectedAuthName(value);
                      const auth = normalizedAuths.find(a => a.authorizationId === id);
                      if (auth?.clientName) {
                        const c = normalizedClients.find(x => x.clientId === auth.clientName);
                        if (c) {
                          setSelectedClientId(c.clientId);
                          setSelectedClientName(c.clientName);
                        }
                      }
                    }}
                    isReadOnly={false}
                  />
                  <Typography variant="caption" display="block" color="primary">
                    Required
                  </Typography>
                </Box>
              )}
            </Box>
            <DialogActions sx={{ p: '1.25rem' }}>
              {isLoadingModal && <Spinner />}
              {!isLoadingModal && (
                <>
                  <Button onClick={() => setShowCreate(false)} sx={{ color: '#41B7C0', '&:hover': { color: '#3d959c' } }}>
                    Cancel
                  </Button>
                  <Button
                    onClick={createRP}
                    color="primary"
                    sx={{ backgroundColor: '#41B7C0', '&:hover': { backgroundColor: '#3d959c' } }}
                    type="submit"
                    variant="contained"
                    disabled={
                      !canEditRecurringPayables ||
                      selectedClientId == null ||
                      (payToChoice === PayToChoiceOptions.Vendor && (!selectedVendorId || selectedVendorId === '')) ||
                      (payToChoice === PayToChoiceOptions.EE && (selectedAuthId == null || selectedAuthId === ''))
                    }
                  >
                    Create Recurring Payable
                  </Button>
                </>
              )}
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </>
  );
};

const mapStateToProps = state => {
  const {
    authorizations: { authorizationSummaries, vendors },
    enumerations,
    clients: { clients },
    expenses: { recurringPayables }
  } = state;

  return {
    recurringPayables,
    authorizationSummaries,
    vendors,
    clients,
    enumerations
  };
};

export default compose(
  withStyles(styles),
  withRouter,
  connect(mapStateToProps, {
    setPageTitle,
    showToast,
    getAllVoucherRecurringPayable: getAllRecurringPayableSummaries,
    postVoucherRecurringPayable: postRecurringPayable,
    getAuthorizationSummaries,
    getVendors,
    getAllEnumerations
  })
)(RecurringPayablesView);
